import React, { ReactElement, useEffect, useState } from 'react';
import { Fade, useMediaQuery, IconButton } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { StatisticsView } from '@components';
import {
    StyledSmallStatisticsContainer,
    StyledToggleBar,
    StyledStatisticsViewWrapper,
    StyledPanel,
} from './statistics-container.styled';
import { TStatisticsContainer } from './statistics-container.types';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const resetBigStatisticsScroll = () => {
    const statisticsStyledBottom = document.querySelector(
        '#statistics-styled-bottom'
    );
    if (statisticsStyledBottom) {
        statisticsStyledBottom.scrollTop = 0;
    }
};

const resetSmallStatisticsScroll = (): boolean => {
    const statisticsViewWrapper = document.querySelector(
        '#statistics-view-wrapper'
    );
    if (statisticsViewWrapper) {
        statisticsViewWrapper.scrollTop = 0;
        return true;
    }
    return false;
};

export const resetStatisticsScroll = () => {
    if (resetSmallStatisticsScroll()) {
        return;
    }
    resetBigStatisticsScroll();
};

export function StatisticsContainer({
    filter,
    filterCategory,
    globalStatistics,
    selectedCountry,
    clearSelectedCountry,
    selectedArea,
}: TStatisticsContainer): ReactElement {
    if (!selectedCountry && !globalStatistics) return <></>;

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [isOpen, setIsOpen] = useState(false);

    const toggleDrawer = () => {
        setIsOpen(previous => !previous);
        resetSmallStatisticsScroll();
    };

    useEffect(() => {
        setIsScrolledToBottom(false);
    }, [selectedCountry]);

    useEffect(() => {
        if (selectedArea) {
            setIsOpen(false);
        }
    }, [selectedArea]);

    const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const scrollContainer = event.currentTarget;
        const { scrollHeight, scrollTop, clientHeight } = scrollContainer;
        const SCROLL_THRESHOLD = 10;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        const hasReachedBottom = distanceFromBottom <= SCROLL_THRESHOLD;
        setIsScrolledToBottom(hasReachedBottom);
    };

    return (
        <div>
            {isSmallScreen ? (
                <StyledSmallStatisticsContainer
                    style={{
                        backgroundColor: theme.palette.background.paper,
                        height: !isOpen
                            ? '160px'
                            : selectedCountry
                            ? '440px'
                            : '300px',
                        transition: theme.transitions.create('height', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    }}
                >
                    {/* Toggle button pinned to top of panel */}
                    <StyledToggleBar
                        onClick={toggleDrawer}
                        style={{
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <IconButton
                            size="small"
                            aria-label={
                                isOpen
                                    ? 'Collapse statistics'
                                    : 'Expand statistics'
                            }
                        >
                            {isOpen ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                        </IconButton>
                    </StyledToggleBar>

                    {/* Single StatisticsView, clipped by parent height */}
                    <StyledStatisticsViewWrapper
                        id="statistics-view-wrapper"
                        style={{
                            overflowY: isOpen ? 'auto' : 'hidden',
                        }}
                        onScroll={handleScroll}
                        $showFade={
                            !isScrolledToBottom &&
                            isOpen &&
                            selectedCountry !== undefined
                        }
                    >
                        <StatisticsView
                            filter={filter}
                            filterCategory={filterCategory}
                            globalStatistics={globalStatistics}
                            selectedCountry={selectedCountry}
                            clearSelectedCountry={clearSelectedCountry}
                        />
                    </StyledStatisticsViewWrapper>
                </StyledSmallStatisticsContainer>
            ) : (
                <div>
                    <Fade
                        in={!selectedCountry}
                        timeout={500}
                        mountOnEnter
                        unmountOnExit
                    >
                        <StyledPanel>
                            <StatisticsView
                                filter={filter}
                                filterCategory={filterCategory}
                                globalStatistics={globalStatistics}
                                selectedCountry={selectedCountry}
                                clearSelectedCountry={clearSelectedCountry}
                            />
                        </StyledPanel>
                    </Fade>
                    <Fade
                        in={Boolean(selectedCountry)}
                        timeout={500}
                        mountOnEnter
                        unmountOnExit
                    >
                        <StyledPanel>
                            <StatisticsView
                                filter={filter}
                                filterCategory={filterCategory}
                                globalStatistics={globalStatistics}
                                selectedCountry={selectedCountry}
                                clearSelectedCountry={clearSelectedCountry}
                            />
                        </StyledPanel>
                    </Fade>
                </div>
            )}
        </div>
    );
}
